import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductsService } from '../../../services/products.service';
import { Product, ProductFormData } from '../../../models/Product';
import { ErrorService } from '../../../shared/services/error.service';

@Component({
  selector: 'app-product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['./product-form.component.css']
})
export class ProductFormComponent implements OnInit {
  productForm: FormGroup;
  isEditMode = false;
  isLoading = false;
  productId: number | null = null;
  product: Product | null = null;

  // Opções para selects
  productTypes = [
    { value: 'Plan', label: 'Plano' },
    { value: 'Service', label: 'Serviço' },
    { value: 'Product', label: 'Produto' },
    { value: 'Subscription', label: 'Assinatura' }
  ];

  billingCycles = [
    { value: 'Por Unidade', label: 'Por Unidade' },
    { value: 'Diário', label: 'Diário' },
    { value: 'Semanal', label: 'Semanal' },
    { value: 'Quinzenal', label: 'Quinzenal' },
    { value: 'Mensal', label: 'Mensal' },
    { value: 'Trimestral', label: 'Trimestral' },
    { value: 'Semestral', label: 'Semestral' },
    { value: 'Anual', label: 'Anual' }
  ];

  paymentBanks = [
    { value: 'SICOOB', label: 'SICOOB' },
    { value: 'SICREDI', label: 'SICREDI' },
    { value: 'BRADESCO', label: 'Bradesco' },
    { value: 'ITAU', label: 'Itaú' },
    { value: 'BB', label: 'Banco do Brasil' },
    { value: 'CAIXA', label: 'Caixa Econômica Federal' },
    { value: 'SANTANDER', label: 'Santander' },
    { value: 'NUBANK', label: 'Nubank' },
    { value: 'INTER', label: 'Inter' },
    { value: 'OUTRO', label: 'Outro' }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private productsService: ProductsService,
    private messageService: ErrorService
  ) {
    this.productForm = this.createForm();
  }

  ngOnInit(): void {
    this.setupTypeRules();

    this.route.params.subscribe(params => {
      if (params['id']) {
        this.productId = +params['id'];
        this.isEditMode = true;
        this.loadProduct();
      }
    });
  }

  createForm(): FormGroup {
    return this.fb.group({
      code: ['', [Validators.required, Validators.minLength(3)]],
      name: ['', [Validators.required, Validators.minLength(3)]],
      type: ['', [Validators.required]],
      billingCycle: ['', [Validators.required]],
      days: [1, [Validators.required, Validators.min(1)]],
      basePrice: [0, [Validators.required, Validators.min(0)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      isActive: [true],
      depositAmount: [null, [Validators.required, Validators.min(0)]],
      includeInsurance: [false],
      includeMaintenance: [false],
      // Configurações do Plano Realize
      isPlanSale: [false],
      installments: [null],
      paymentBank: [null],
      isDepositRefundable: [true],
      includesRoadsideSupport: [true],
      includesVehicleSubstitution: [true],
      exclusiveVehicles: [false],
      requiresChecklist: [true],
      maxVehicles: [null],
      preventiveMaintenanceMonthlyLimit: [null],
      penultimateInstallmentAdditional: [null]
    });
  }

  loadProduct(): void {
    if (!this.productId) return;

    this.isLoading = true;
    this.productsService.getProduct(this.productId).subscribe({
      next: (product) => {
        this.product = product;
        this.populateForm(product);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar produto:', error);
        this.isLoading = false;
        this.messageService.showErrorMessage('Erro ao carregar produto. Tente novamente.');
        this.router.navigate(['/products']);
      }
    });
  }

  populateForm(product: Product): void {
    this.productForm.patchValue({
      code: product.code,
      name: product.name,
      type: product.type,
      billingCycle: product.billingCycle,
      days: product.days,
      basePrice: product.basePrice,
      description: product.description,
      isActive: product.isActive,
      depositAmount: product.depositAmount,
      includeInsurance: product.includeInsurance,
      includeMaintenance: product.includeMaintenance,
      isPlanSale: product.isPlanSale ?? false,
      installments: product.installments ?? null,
      paymentBank: product.paymentBank ?? null,
      isDepositRefundable: product.isDepositRefundable ?? true,
      includesRoadsideSupport: product.includesRoadsideSupport ?? true,
      includesVehicleSubstitution: product.includesVehicleSubstitution ?? true,
      exclusiveVehicles: product.exclusiveVehicles ?? false,
      requiresChecklist: product.requiresChecklist ?? true,
      maxVehicles: product.maxVehicles ?? null,
      preventiveMaintenanceMonthlyLimit: product.preventiveMaintenanceMonthlyLimit ?? null,
      penultimateInstallmentAdditional: product.penultimateInstallmentAdditional ?? null
    });

    this.applyTypeRules(product.type);
  }

  private setupTypeRules(): void {
    this.applyTypeRules(this.productForm.get('type')?.value || '');
    this.productForm.get('type')?.valueChanges.subscribe((type: string) => {
      this.applyTypeRules(type);
    });
  }

  private applyTypeRules(type: string): void {
    const billingCycleControl = this.productForm.get('billingCycle');
    const daysControl = this.productForm.get('days');
    const depositAmountControl = this.productForm.get('depositAmount');
    const includeInsuranceControl = this.productForm.get('includeInsurance');
    const includeMaintenanceControl = this.productForm.get('includeMaintenance');

    if (!billingCycleControl || !daysControl || !depositAmountControl || !includeInsuranceControl || !includeMaintenanceControl) {
      return;
    }

    if (this.requiresBillingDetails(type)) {
      billingCycleControl.enable({ emitEvent: false });
      depositAmountControl.enable({ emitEvent: false });
      includeInsuranceControl.enable({ emitEvent: false });
      includeMaintenanceControl.enable({ emitEvent: false });

      billingCycleControl.setValidators([Validators.required]);
      daysControl.setValidators([Validators.required, Validators.min(1)]);
      depositAmountControl.setValidators([Validators.required, Validators.min(0)]);

      if (billingCycleControl.value === 'Por Unidade') {
        billingCycleControl.setValue('', { emitEvent: false });
      }
    } else {
      billingCycleControl.setValue('Por Unidade', { emitEvent: false });
      billingCycleControl.disable({ emitEvent: false });

      daysControl.setValue(1, { emitEvent: false });
      depositAmountControl.setValue(0, { emitEvent: false });
      includeInsuranceControl.setValue(false, { emitEvent: false });
      includeMaintenanceControl.setValue(false, { emitEvent: false });

      billingCycleControl.clearValidators();
      daysControl.clearValidators();
      depositAmountControl.clearValidators();
    }

    billingCycleControl.updateValueAndValidity({ emitEvent: false });
    daysControl.updateValueAndValidity({ emitEvent: false });
    depositAmountControl.updateValueAndValidity({ emitEvent: false });
  }

  requiresBillingDetails(type?: string): boolean {
    return type === 'Plan' || type === 'Subscription';
  }

  get isPlanSaleChecked(): boolean {
    return !!this.productForm.get('isPlanSale')?.value;
  }

  onSubmit(): void {
    if (this.productForm.valid && !this.isLoading) {
      this.isLoading = true;
      
      const formData: ProductFormData = this.productForm.getRawValue();
      
      if (this.isEditMode && this.productId) {
        this.updateProduct(formData);
      } else {
        this.createProduct(formData);
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  createProduct(formData: ProductFormData): void {
    this.productsService.createProduct(formData).subscribe({
      next: (newProduct) => {
        this.messageService.showInfoMessage('Produto criado com sucesso!');
        this.router.navigate(['/products']);
      },
      error: (error) => {
        console.error('Erro ao criar produto:', error);
        this.isLoading = false;
        this.messageService.showErrorMessage('Erro ao criar produto. Tente novamente.');
      }
    });
  }

  updateProduct(formData: ProductFormData): void {
    if (!this.productId) return;

    this.productsService.updateProduct(this.productId, formData).subscribe({
      next: (updatedProduct) => {
        this.messageService.showInfoMessage('Produto atualizado com sucesso!');
        this.router.navigate(['/products']);
      },
      error: (error) => {
        console.error('Erro ao atualizar produto:', error);
        this.isLoading = false;
        this.messageService.showErrorMessage('Erro ao atualizar produto. Tente novamente.');
      }
    });
  }

  markFormGroupTouched(): void {
    Object.keys(this.productForm.controls).forEach(key => {
      const control = this.productForm.get(key);
      control?.markAsTouched();
    });
  }

  cancel(): void {
    this.router.navigate(['/products']);
  }

  getFieldError(fieldName: string): string {
    const field = this.productForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return `${this.getFieldLabel(fieldName)} é obrigatório`;
      }
      if (field.errors['minlength']) {
        return `${this.getFieldLabel(fieldName)} deve ter pelo menos ${field.errors['minlength'].requiredLength} caracteres`;
      }
      if (field.errors['min']) {
        return `${this.getFieldLabel(fieldName)} deve ser maior que ${field.errors['min'].min}`;
      }
    }
    return '';
  }

  getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      code: 'Código',
      name: 'Nome',
      type: 'Tipo',
      billingCycle: 'Ciclo de Cobrança',
      days: 'Duração',
      basePrice: 'Preço Base',
      description: 'Descrição',
      depositAmount: 'Valor da Caução',
      installments: 'Número de Parcelas',
      paymentBank: 'Banco de Pagamento',
      maxVehicles: 'Limite de Veículos',
      preventiveMaintenanceMonthlyLimit: 'Preventivas por Mês',
      penultimateInstallmentAdditional: 'Acréscimo na Penúltima Parcela'
    };
    return labels[fieldName] || fieldName;
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Editar Produto' : 'Novo Produto';
  }

  get submitButtonText(): string {
    if (this.isLoading) {
      return this.isEditMode ? 'Salvando...' : 'Criando...';
    }
    return this.isEditMode ? 'Salvar Alterações' : 'Criar Produto';
  }
}
